import { Capacitor } from '@capacitor/core';

import { requestAuthKey, requestLoginApp } from '@/api/apiService';
import api from '@/api/index';
import { useUserStore } from '@/stores/user';
import { TypeLogin, TypeLoginChannel, TypeUser } from '@/types/common';
import { IErrorLogin, ILoginForm } from '@/types/components';
import { IAppError, ILocalLoginError } from '@/types/response';
import { refreshedCredentialsNative } from '@/utils/authService';
import { isInCapacitorApp } from '@/utils/device';
import { encryptPassword } from '@/utils/encryptor';
import { navigateInApp } from '@/utils/native/nativeBridge';
import { validateSubUserId, validateSubUserPw, validatePartnerCode } from '@/utils/validators';

const isLocalTest = import.meta.env.VITE_IS_MOBILE_LOCAL === 'true';

export const useLogin = () => {
  const userStore = useUserStore();
  const router = useRouter();

  // 로그인 입력란
  const loginForm = ref<ILoginForm>({
    userId: '',
    password: '',
    partnerCode: '',
    loginType: '',
  });

  const errorLogin = ref<IErrorLogin | ILocalLoginError>();

  const baseURL = api.defaults.baseURL ?? '';

  const domain = Capacitor.getPlatform() === 'ios' ? 'capacitor://localhost/' : 'https://localhost/';

  const handleLogin = async () => {
    try {
      const isFormValid = validateLogin();

      if (!isFormValid) return;

      const loginType = getLoginType();
      const loginChannel = getLoginChannel();
      const partnerCode = loginForm.value.partnerCode;
      const password = await getEncryptedPassword();

      const { data, success } = await requestLoginApp(
        {
          userId: loginForm.value.userId,
          password,
        },
        { type: loginType, partnerCode, loginChannel }
      );

      if (success) {
        await userStore.setLoginInfo({ ...data, loginType, partnerCode });
        await userStore.fetchUserInfo();
        movePage(data.userType);
      }
    } catch (error) {
      handleErrorLogin(error as IAppError);
    }
  };

  const validateLogin = (): boolean => {
    errorLogin.value = undefined;

    const errorPartnerCode = validatePartnerCode(loginForm.value.partnerCode);

    if (errorPartnerCode) {
      errorLogin.value = {
        errorCode: 1015, // TODO 다른 에러코드 필요
        messageDisplayed: 'login_select_partner_code',
      };
      return false;
    }

    const errorId = validateSubUserId(loginForm.value.userId);
    if (errorId) {
      errorLogin.value = {
        errorCode: 1004,
        messageDisplayed: 'failed_to_login_by_id_or_pw',
      };
      return false;
    }

    const errorPassword = validateSubUserPw(loginForm.value.password);
    if (errorPassword) {
      errorLogin.value = {
        errorCode: 1002,
        messageDisplayed: 'failed_to_login_by_id_or_pw',
      };
      return false;
    }

    return true;
  };

  // 에러메시지
  const handleErrorLogin = (responseError: IAppError) => {
    let messageDisplayed = '';
    switch (responseError.errorCode) {
      case 1001:
        messageDisplayed = 'invalid_access';
        break;
      case 1002:
      case 1004:
        messageDisplayed = 'failed_to_login_by_id_or_pw';
        break;
      case 1008:
        messageDisplayed = 'invalid_access';
        break;
      case 1015:
        messageDisplayed = 'login_no_such_partner_code';
        break;
      default:
        messageDisplayed = 'failed_to_login_for_unknown_reason';
        break;
    }
    errorLogin.value = { ...responseError, messageDisplayed };
  };

  const handleLoginWithRefreshToken = async (): Promise<boolean> => {
    try {
      const loginType = getLoginType();
      const credentials = await refreshedCredentialsNative();
      const { accessToken, refreshToken } = credentials;

      await userStore.setLoginInfo({ ...credentials, loginType, token: accessToken, refreshToken });
      await userStore.fetchUserInfo();

      if (isLocalTest) {
        router.push({ name: 'app-main-view' });
      } else {
        movePage(credentials.userType);
      }
      return true;
    } catch {
      throw Error('리프레시 토큰으로 로그인하기 핸들러가 실패했습니다');
    }
  };

  // 페이지 이동
  const movePage = (userType: TypeUser) => {
    const isMobile = isInCapacitorApp();

    if (isMobile) {
      navigateInApp(`${baseURL}/app/main`);
      return;
    }

    const { type, redirectTo } = getQueryStrings();

    let path = '';

    if (redirectTo) {
      path = redirectTo.toString();
    } else if (type) {
      path =
        {
          admin: userType === 'HOST' ? '/admin/owner' : '/admin/employee',
          dist: '/dist/dist-dashboard',
          internal: '/internal/toolkit',
          // ss: '/ss/dashboard', TODO 아래 삭제 및 주석 해제
          ss: '/admin/employee',
        }[type.toString()] || '';
    }

    navigateInApp(`${baseURL + path}`);
  };

  // 비밀번호 암호화
  const getEncryptedPassword = async (): Promise<string> => {
    localStorage.removeItem('SC-USER-ID');
    localStorage.removeItem('authToken');
    sessionStorage.removeItem('authToken');

    const { data: { partnerPublicKey = '' } = { partnerPublicKey: '' } } = await requestAuthKey();
    return await encryptPassword(partnerPublicKey, loginForm.value.password);
  };

  const getLoginType = (): TypeLogin => {
    if (isInCapacitorApp()) return 'SELF_SERVICE';
    if (isMatchLoginRoute('dist')) return 'DISTRIBUTOR';

    if (isMatchLoginRoute('internal')) return 'HEAD_OFFICE';
    if (isMatchLoginRoute('admin') || loginForm.value.loginType === 'CUSTOMER') return 'CUSTOMER';

    //채팅방 진입 전, 로그인
    const { partnerType } = getQueryStrings();
    if (isMatchLoginRoute('chat') && partnerType) return partnerType as TypeLogin;

    return 'CUSTOMER';
  };

  const getLoginChannel = (): TypeLoginChannel => {
    if (isInCapacitorApp()) return 'SELF_SERVICE';
    if (isMatchLoginRoute('chat')) return 'CHAT';
    if (isMatchLoginRoute('mobile')) return 'CHAT';
    return 'OPERATOR';
  };

  const isMatchLoginRoute = (key: string) => {
    const { type = '', redirectTo = '' } = getQueryStrings();
    return type === key || (redirectTo && redirectTo.includes(key));
  };

  const getQueryStrings = () => {
    const params = new URLSearchParams(window.location.search);
    const type = params.get('type') ?? '';
    const redirectTo = params.get('redirectTo') ?? '';
    const partnerType = params.get('partnerType') ?? '';
    return { type, redirectTo, partnerType };
  };

  const goToLoginPage = () => {
    navigateInApp(`${domain}app/login`);
  };

  return {
    loginForm,
    errorLogin,
    handleLogin,
    handleLoginWithRefreshToken,
    goToLoginPage,
  };
};
